import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material';
import { env } from '@/api/env';
import { usePermissions } from '@/auth/usePermissions';
import {
  useCurrentUserQuery,
  useResendVerificationEmailMutation,
} from '@/features/useraccount/api';

export function HomePage() {
  const { t } = useTranslation();
  const { logout } = useAuth0();
  const { claims } = usePermissions();
  useCurrentUserQuery();
  const resendVerification = useResendVerificationEmailMutation();

  const namespace = env.auth0.namespace;
  const emailVerified = claims?.[`${namespace}email_verified`] === true;
  const sub = typeof claims?.sub === 'string' ? claims.sub : null;
  const needsVerification = !!claims && !emailVerified && !!sub;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [resultOpen, setResultOpen] = useState<null | 'success' | 'error'>(null);

  useEffect(() => {
    if (needsVerification) setDialogOpen(true);
  }, [needsVerification]);

  const finishWithLogout = () => {
    logout({ logoutParams: { returnTo: env.app.dioolBaseUrl } });
  };

  const onResend = async () => {
    if (!sub) return;
    setDialogOpen(false);
    try {
      const res = await resendVerification.mutateAsync({ user_id: sub });
      setResultOpen(res.code === 0 ? 'success' : 'error');
    } catch {
      setResultOpen('error');
    }
  };

  return (
    <>
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h4" gutterBottom>
          {t('pages.home.welcome', { defaultValue: 'Welcome' })}
        </Typography>
        <Box
          component="img"
          src="/assets/images/diool_welcome.jpg"
          alt=""
          sx={{ maxWidth: '100%', height: 'auto', mt: 2 }}
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).style.display = 'none';
          }}
        />
      </Box>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>
          {t('messages.email-not-verified', { defaultValue: 'Email not verified' })}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('messages.verify-email', { defaultValue: 'Please verify your email.' })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDialogOpen(false);
              finishWithLogout();
            }}
          >
            Ok
          </Button>
          <Button onClick={onResend} variant="contained">
            {t('messages.resend-verification-email', {
              defaultValue: 'Resend verification email',
            })}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={resultOpen !== null} onClose={finishWithLogout}>
        <DialogTitle>
          {resultOpen === 'success'
            ? t('messages.email-sended', { defaultValue: 'Email sent' })
            : t('messages.error', { defaultValue: 'Error' })}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {resultOpen === 'success'
              ? t('messages.email-not-verified-alert', {
                  defaultValue: 'Please check your inbox to verify your email.',
                })
              : t('messages.unknown-error', { defaultValue: 'Unknown error' })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={finishWithLogout} variant="contained">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
